import {
	useDocumentStages,
	DocumentStagesData,
} from "@/hooks/useDocumentStages"
import { Checkbox } from "@/components/ui/checkbox"
import { FileText } from "lucide-react"
import { FC, useState } from "react"
import { Document } from "@/types"
import { cn } from "@/lib/utils"
import { formatDistanceToNow } from "date-fns"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import {
	MoreHorizontal,
	Eye,
	CheckCircle2,
	XCircle,
	Trash2,
	Download,
} from "lucide-react"
import { SummaryDialog } from "./DocumentSummary"
import { formatFileSize } from "@/utils/formatFileSize"
import { DocumentViewerModal } from "./DocumentViewerModal"
import { useDocumentDownload } from "@/hooks/useDocumentDownload"

type DocumentRowProps = {
	document: Document
	caseId: string
	isSelected: boolean
	onToggleSelect: () => void
	onDelete: () => Promise<void>
	stagesData?: DocumentStagesData
}

export const DocumentRow: FC<DocumentRowProps> = ({
	document: documentData,
	caseId,
	isSelected,
	onToggleSelect,
	onDelete,
	stagesData: propStagesData,
}) => {
	const { data: fetchedStagesData } = useDocumentStages(
		propStagesData ? undefined : caseId,
		propStagesData ? undefined : documentData.id
	)
	const stagesData = propStagesData || fetchedStagesData
	const [isDeleting, setIsDeleting] = useState(false)
	const [isSummaryOpen, setIsSummaryOpen] = useState(false)
	const [viewingDocument, setViewingDocument] = useState<boolean>(false)
	const { downloadDocument, isDownloading } = useDocumentDownload()

	// Check document relevance status
	const isRelevant = stagesData?.stage3?.is_relevant
	const isIrrelevant = stagesData?.stage3?.is_relevant === false
	const hasRelevanceStatus = isRelevant || isIrrelevant

	const handleDelete = async () => {
		setIsDeleting(true)
		try {
			await onDelete()
		} catch (error) {
			console.error("Delete failed:", error)
		} finally {
			setIsDeleting(false)
		}
	}

	const handleDownload = () => {
		downloadDocument(caseId, documentData)
	}

	const getTypeIcon = (extension: string) => {
		switch (extension.toLowerCase()) {
			case "pdf":
				return <FileText className="h-4 w-4 text-red-500" />
			case "docx":
			case "doc":
				return <FileText className="h-4 w-4 text-blue-500" />
			case "xlsx":
			case "xls":
				return <FileText className="h-4 w-4 text-green-500" />
			default:
				return <FileText className="h-4 w-4 text-gray-500" />
		}
	}

	return (
		<>
			<DocumentViewerModal
				isOpen={viewingDocument}
				onClose={() => setViewingDocument(false)}
				document={documentData}
				caseId={caseId}
			/>
			<SummaryDialog
				summary={stagesData?.stage2.full_summary}
				open={isSummaryOpen}
				onOpenChange={setIsSummaryOpen}
			/>
			<tr
				className={cn("border-b hover:bg-muted/50", isSelected && "bg-muted")}
			>
				<td className="w-[40px] p-4 align-middle">
					<Checkbox
						checked={isSelected}
						onCheckedChange={onToggleSelect}
						aria-label={`Select ${documentData.name}`}
					/>
				</td>
				<td className="w-[30%] p-4 align-middle font-medium">
					<div
						className="flex items-center cursor-pointer hover:underline"
						onClick={() => setViewingDocument(true)}
					>
						{documentData.name}
					</div>
					{documentData.upload_location && (
						<div
							className="text-xs text-muted-foreground mt-1 truncate max-w-[300px]"
							title={documentData.upload_location}
						>
							{documentData.upload_location}
						</div>
					)}
				</td>
				<td className="w-[120px] p-4 align-middle">
					<div className="flex items-center">
						{getTypeIcon(documentData.file_extension)}
						<div className="ml-2">
							<div className="font-medium">
								{stagesData?.stage1?.understood_file_type ||
									documentData.file_extension.toUpperCase()}
							</div>
							{stagesData?.stage1?.understood_file_type &&
								stagesData.stage1.understood_file_type !==
									documentData.file_extension.toUpperCase() && (
									<div className="text-xs text-muted-foreground">
										{documentData.file_extension.toUpperCase()}
									</div>
								)}
						</div>
					</div>
				</td>
				<td className="w-[100px] p-4 align-middle">
					{formatFileSize(documentData.size)}
				</td>
				<td className="w-[150px] p-4 align-middle">
					{formatDistanceToNow(documentData.created_at, { addSuffix: true })}
				</td>
				<td className="w-[80px] p-4 align-middle">
					{hasRelevanceStatus ? (
						<div className="flex items-center">
							{isRelevant ? (
								<div className="flex items-center text-green-600">
									<CheckCircle2 className="h-4 w-4 mr-1" />
									<span className="text-xs">Relevant</span>
								</div>
							) : (
								<div className="flex items-center text-red-600">
									<XCircle className="h-4 w-4 mr-1" />
									<span className="text-xs">Irrelevant</span>
								</div>
							)}
						</div>
					) : (
						<span className="text-xs text-muted-foreground">Processing</span>
					)}
				</td>
				{/* Search Match Column - always rendered when in search mode */}
				{documentData.relevance_to_search && (
					<td className="w-[100px] p-4 align-middle">
						<div
							className={cn(
								"text-xs font-medium py-1 px-2 rounded-md inline-block",
								documentData.relevance_to_search === "Exact Match"
									? "bg-purple-100 text-purple-800"
									: documentData.relevance_to_search === "Partial Match"
									? "bg-blue-100 text-blue-800"
									: "bg-gray-100 text-gray-800"
							)}
						>
							{documentData.relevance_to_search}
						</div>
					</td>
				)}
				<td className="w-[80px] p-4 align-middle text-right">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="icon" className="h-8 w-8">
								<MoreHorizontal className="h-4 w-4" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={() => setViewingDocument(true)}>
								<Eye className="mr-2 h-4 w-4" />
								View
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => setIsSummaryOpen(true)}>
								<FileText className="mr-2 h-4 w-4" />
								View Summary
							</DropdownMenuItem>
							<DropdownMenuItem
								onClick={handleDownload}
								disabled={isDownloading}
							>
								<Download className="mr-2 h-4 w-4" />
								{isDownloading ? "Downloading..." : "Download"}
							</DropdownMenuItem>
							<DropdownMenuItem
								className="text-destructive focus:text-destructive focus:bg-destructive/10"
								onClick={handleDelete}
								disabled={isDeleting}
							>
								<Trash2 className="mr-2 h-4 w-4" />
								{isDeleting ? "Deleting..." : "Delete"}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</td>
			</tr>
		</>
	)
}
